// Disabling the rule for inline styles since this animation heavily relies on CSS variables.
/* oxlint-disable react/forbid-component-props */
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { Button } from '@shipt/design-system-buttons'
import { Box, Column, Row } from '@shipt/design-system-layouts'
import { Display, Body } from '@shipt/design-system-typography'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'
import { screenSizes } from '@shipt/design-system-themes'
import { routes } from '@shared/constants/routes'
import { getTargetCircleQueryParams } from '@/features/shared/components/Headers/Circle360Header/utils'
import { useSearchParams } from 'next/navigation'
import { keyframes } from '@/features/shared/styles/effects'
import { theme } from '@/features/shared/constants/shiptTheme'

declare module 'csstype' {
  interface Properties {
    // Allow any CSS Custom Properties
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    [index: `--${string}`]: any
  }
}

type Props = { content_type_id: string; id: string }

const STORES_IN_BAG = ['petco', 'lowes-foods', 'meijer'] as const
const ROWS = [
  {
    index: '3',
    stores: ['7-eleven', 'bashas-food', 'cub', 'cvs', 'dierbergs'],
    offset: '50px',
  },
  {
    index: '2',
    stores: ['heb', 'jewel-osco', 'lowes-foods', 'lowes-home-improvement', 'lunds-and-byerlys'],
    offset: '-125px',
  },
  {
    index: '1',
    stores: ['meijer', 'petco', 'petsmart', 'savemart', 'weis-markets'],
    offset: '50px',
  },
]

const STORES_ANIMATION_CONFIG: Record<string, { index: number; delay: number }> = {
  'jewel-osco': { index: 1, delay: 1550 },
  'lowes-foods': { index: 0, delay: 1450 },
  'lowes-home-improvement': { index: -1, delay: 1500 },
  'lunds-and-byerlys': { index: -2, delay: 1600 },
  meijer: { index: 2, delay: 1400 },
  petco: { index: 1, delay: 1300 },
  petsmart: { index: 0, delay: 1350 },
  savemart: { index: -1, delay: 1450 },
}

export const AnimatedHero = ({ content_type_id, id }: Props) => {
  const searchParams = useSearchParams()
  const params = getTargetCircleQueryParams(searchParams)

  return (
    <HeroGridLayoutContainer {...getCMSWrapperProps({ content_type_id, id })}>
      <Hero className="fullWidth contentAtLgMobile">
        <StoreGridLayer>
          <Column spacing="xl">
            {ROWS.map(({ stores, offset, index }) => {
              return (
                <StoresGridRow key={index} style={{ '--row-offset': offset, '--row-index': index }}>
                  {stores.map((store) => {
                    const configForStore = STORES_ANIMATION_CONFIG[store]

                    if (!configForStore) {
                      return (
                        <Store
                          key={`${index}-${store}`}
                          src={objectStorageUrl(`animation-logo-${store}.png`)}
                          alt=""
                          height={200}
                          width={200}
                        />
                      )
                    }

                    return (
                      <StoreWrapper
                        key={`${index}-${store}`}
                        style={{
                          '--store-index': configForStore.index,
                          '--delay': `${configForStore.delay}ms`,
                        }}
                      >
                        <Store
                          src={objectStorageUrl(`animation-logo-${store}.png`)}
                          alt=""
                          height={200}
                          width={200}
                        />
                      </StoreWrapper>
                    )
                  })}
                </StoresGridRow>
              )
            })}
          </Column>
        </StoreGridLayer>

        <BagLayer>
          <BagBackdrop />
          <GroceryBag>
            <Row justify="center">
              {STORES_IN_BAG.map((store, i) => {
                return (
                  <StoreInBag
                    key={store}
                    src={objectStorageUrl(`animation-logo-${store}.png`)}
                    alt=""
                    width={70}
                    height={70}
                    style={{ '--index': i }}
                  />
                )
              })}
            </Row>
          </GroceryBag>
          <GroceryBag>
            <Image src={objectStorageUrl('bag.png')} alt="" width={180} height={180} />
          </GroceryBag>
        </BagLayer>

        <CityscapeImage
          src={objectStorageUrl('C360_Environment.png')}
          alt=""
          width={1440}
          height={300}
        />

        <ContentLayer padding={{ base: ['lg'], sm: ['xl'] }}>
          <Box display={{ base: 'block', md: 'none' }}>
            <Display align="center" className="hero_heading_line1">
              All the stores you&nbsp;trust,
            </Display>
            <Display align="center" className="hero_heading_line2">
              delivered to your&nbsp;door
            </Display>
          </Box>
          <Box display={{ base: 'none', md: 'block' }}>
            <Display align="center" size="xl" className="hero_heading_line1">
              All the stores you&nbsp;trust,
            </Display>
            <Display align="center" size="xl" className="hero_heading_line2">
              delivered to your&nbsp;door
            </Display>
          </Box>
          <Column align="center" marginTop="xl" spacing="xl">
            <Body
              align="center"
              className="hero_subheading"
              size={{ base: 'xl', md: 'xxl' }}
              weight="medium"
            >
              Unlock free access with your Target Circle&nbsp;360&nbsp;account
            </Body>
            <CTAButton size="lg" forwardedAs="a" href={`${routes.LOGIN.url}${params}`} isLink>
              Start shopping
            </CTAButton>
          </Column>
        </ContentLayer>
      </Hero>
    </HeroGridLayoutContainer>
  )
}

const HeroGridLayoutContainer = styled(GridLayoutContainer)`
  --content-max-width: 1440px;
  width: 100%;

  @media (prefers-reduced-motion: reduce) {
    & * {
      animation-duration: 0s !important;
      animation-delay: 0s !important;
    }
  }
`

const GroceryBag = styled(Box)`
  grid-area: grocery-bag;
  z-index: 1;
  height: 180px;
  width: 180px;
`

const BagBackdrop = styled(Box)`
  position: absolute;
  top: 50%;
  bottom: 0;
  width: 100%;
  background-color: ${theme.color.background.base.neutral.subtle_100};
`

const StoreInBag = styled(Image)`
  flex-shrink: 0;
  display: block;
  --to-y: -50px;
  animation: ${keyframes.translateTo} 107ms calc(2550ms + (var(--index) * 107ms)) forwards ease-in;
  border-radius: 20%;

  &:not(:first-child) {
    margin-left: -20px;
  }
`

const StoresGridRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, var(--square-dimension));
  gap: var(--stores-grid-gap);
  --to-x: var(--row-offset);
  --to-y: 0;
  animation: ${keyframes.translateTo} 1050ms ease-in-out forwards;

  &:nth-child(odd) {
    transform: translateX(-100%);
  }

  &:nth-child(even) {
    transform: translateX(100%);
  }
`

const Store = styled(Image)`
  display: block;
  height: var(--square-dimension);
  width: var(--square-dimension);
  border-radius: 20%;
`

const StoreWrapper = styled.div`
  --distance-to-bag: 500px;
  --x: calc(
    ((var(--square-dimension) + var(--stores-grid-gap)) * var(--store-index)) - var(--row-offset, 0)
  );
  --y: calc((var(--square-dimension) + 24px) * var(--row-index, 1) + var(--distance-to-bag));

  --to-x: var(--x);
  --to-y: 0;
  animation: ${keyframes.translateTo} 500ms var(--delay, 0s) ease-in-out forwards;

  ${Store} {
    --to-x: 0;
    --to-y: var(--y);
    --to-scale: 0.2;
    --fade-to: 0;
    animation:
      ${keyframes.translateTo} 1000ms var(--delay, 0s) ease-in-out forwards,
      ${keyframes.fadeTo} 10ms 3.4s linear forwards;
  }
`

const CTAButton = styled(Button)`
  opacity: 0;
  animation: ${keyframes.fadeTo} 570ms 4650ms ease-in-out forwards;
`

const Hero = styled(Box)`
  background-color: ${theme.color.background.base.neutral.subtle_100};
  display: grid;
  grid-template-columns: [hero-start] 100% [hero-end];
  grid-template-rows: [hero-start] min(800px, 100svh) [hero-end];
  gap: 8px;
  overflow: hidden;
  --hero-height: 527px;
  animation: ${keyframes.targetC360HeroHeight} 1s 3520ms ease-in-out forwards;

  @media ${screenSizes.lgMobile} {
    border-radius: 24px;
    --hero-height: 627px;
  }
`

const StoreGridLayer = styled(Box)`
  --square-dimension: 200px;
  --stores-grid-gap: 50px;
  grid-area: hero;
  place-self: center;
  transform: translateY(0);
  --to-y: -1000px;
  animation: ${keyframes.translateTo} 1400ms 1450ms ease-in-out forwards;

  @media ${screenSizes.lgMobile} {
    --square-dimension: 220px;
  }
`

const BagLayer = styled(Row)`
  grid-area: hero;
  display: grid;
  place-items: center;
  grid-template-areas: 'grocery-bag';
  transform: translateY(500px);
  animation:
    ${keyframes.targetC360ToRestingState} 1100ms 1920ms forwards cubic-bezier(0, 0.55, 0.45, 1),
    ${keyframes.targetC360BagExit} 460ms 3870ms forwards ease-in-out;
`

const ContentLayer = styled(Box)`
  display: grid;
  place-content: center;
  place-items: center;
  grid-area: hero;

  .hero_heading_line1 {
    --to-y: -100px;
    transform: translateY(-600px);
    animation:
      ${keyframes.translateTo} 520ms 2180ms ease-in-out forwards,
      ${keyframes.targetC360ToRestingState} 1s 3870ms ease-in-out forwards;
  }

  .hero_heading_line2 {
    --to-y: 200px;
    transform: translateY(800px);
    animation:
      ${keyframes.translateTo} 482ms 2520ms ease-in-out forwards,
      ${keyframes.targetC360ToRestingState} 1s 3870ms ease-in-out forwards;
  }

  .hero_subheading {
    opacity: 0;
    animation: ${keyframes.fadeTo} 570ms 4650ms ease-in-out forwards;
  }

  @media ${screenSizes.tablet} {
    .hero_heading_line1 {
      --to-y: -200%;
      transform: translateY(-500%);
    }

    .hero_heading_line2 {
      --to-y: 300%;
      transform: translateY(700%);
    }
  }
`

const CityscapeImage = styled(Image)`
  align-self: end;
  grid-area: hero;
  width: 100%;
  height: auto;
  transform: translateY(100%);
  animation: ${keyframes.translateTo} 670ms ease-in-out 4280ms forwards;
`
